/**
 * Offline write queue — FIFO of pending mutations backed by IndexedDB.
 *
 * Every entry represents one HTTP write (POST / PUT / PATCH / DELETE) that
 * should eventually reach the server. The sync worker (see syncWorker.ts)
 * drains this queue whenever the browser regains connectivity.
 *
 * IndexedDB is used via `idb-keyval` so we don't have to hand-roll cursors
 * or transactions. The whole queue lives under a single key as a JSON array;
 * concurrent writes serialise through a small in-process promise chain so
 * `enqueue()` and `remove()` never race with each other.
 */
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';

const QUEUE_KEY = 'erp:offline-queue';

export type HttpMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface QueueItem {
  /** Client-generated id, stable for the lifetime of the queued write. */
  id: string;
  method: HttpMethod;
  /** Absolute path (e.g. `/api/v1/leads`) or full URL. */
  url: string;
  body?: unknown;
  /** Logical entity name, used for cache-invalidation on flush. */
  entity: string;
  /**
   * If this write created a new record locally, the optimistic id we
   * showed the user. Later PATCH/DELETE writes that reference the same
   * `tempId` will have their URLs rewritten to the real server id.
   */
  tempId?: string;
  createdAt: number;
  /**
   * Retry bookkeeping used by the sync worker; callers should leave these
   * unset when first enqueuing.
   */
  attempts?: number;
  nextAttemptAt?: number;
}

// Serialise all queue mutations. IndexedDB itself is transactional, but our
// read-modify-write pattern needs a JS-level lock to be atomic.
let lock: Promise<unknown> = Promise.resolve();
function withLock<T>(fn: () => Promise<T>): Promise<T> {
  const next = lock.then(fn, fn);
  // Swallow errors on the lock chain so one failure doesn't poison future ops.
  lock = next.catch(() => undefined);
  return next;
}

async function readAll(): Promise<QueueItem[]> {
  const raw = (await idbGet(QUEUE_KEY)) as QueueItem[] | undefined;
  return Array.isArray(raw) ? raw : [];
}

async function writeAll(items: QueueItem[]): Promise<void> {
  if (items.length === 0) {
    await idbDel(QUEUE_KEY);
    return;
  }
  await idbSet(QUEUE_KEY, items);
}

/** Append an item to the queue. Duplicate ids are ignored. */
export function enqueue(item: QueueItem): Promise<void> {
  return withLock(async () => {
    const items = await readAll();
    if (items.some((i) => i.id === item.id)) return;
    items.push(item);
    await writeAll(items);
  });
}

/** Return the full queue, oldest first. */
export function list(): Promise<QueueItem[]> {
  return withLock(async () => readAll());
}

/** Remove a single item by id. Safe to call for missing ids. */
export function remove(id: string): Promise<void> {
  return withLock(async () => {
    const items = await readAll();
    const next = items.filter((i) => i.id !== id);
    if (next.length !== items.length) await writeAll(next);
  });
}

/**
 * Persist an updated item in place (for retry bookkeeping). No-op if the id
 * is no longer in the queue.
 */
export function update(item: QueueItem): Promise<void> {
  return withLock(async () => {
    const items = await readAll();
    const idx = items.findIndex((i) => i.id === item.id);
    if (idx < 0) return;
    items[idx] = item;
    await writeAll(items);
  });
}

export function count(): Promise<number> {
  return withLock(async () => (await readAll()).length);
}

export function clear(): Promise<void> {
  return withLock(async () => {
    await idbDel(QUEUE_KEY);
  });
}

/* -------------------------------------------------------------------------- */
/* tempId → serverId map                                                      */
/* -------------------------------------------------------------------------- */
/**
 * When an offline POST eventually reaches the server we learn the real id.
 * Any queued PATCH / DELETE written against the tempId needs its URL
 * rewritten before flush, or it will 404. This map is kept in a separate
 * IndexedDB key so it isn't blown away when the queue drains.
 */
const TEMP_ID_MAP_KEY = 'erp:offline-tempids';

export async function getTempIdMap(): Promise<Record<string, string | number>> {
  const raw = (await idbGet(TEMP_ID_MAP_KEY)) as Record<string, string | number> | undefined;
  return raw && typeof raw === 'object' ? raw : {};
}

export async function setTempIdMapping(tempId: string, serverId: string | number): Promise<void> {
  const map = await getTempIdMap();
  map[tempId] = serverId;
  await idbSet(TEMP_ID_MAP_KEY, map);
}

/* -------------------------------------------------------------------------- */
/* Dead letter — writes the server rejected (4xx) that need human review     */
/* -------------------------------------------------------------------------- */
const DEAD_LETTER_KEY = 'erp:offline-deadletter';

export interface DeadLetterItem extends QueueItem {
  failedAt: number;
  status: number;
  reason?: string;
}

export async function pushDeadLetter(item: DeadLetterItem): Promise<void> {
  const raw = (await idbGet(DEAD_LETTER_KEY)) as DeadLetterItem[] | undefined;
  const arr = Array.isArray(raw) ? raw : [];
  arr.push(item);
  await idbSet(DEAD_LETTER_KEY, arr);
}

export async function listDeadLetter(): Promise<DeadLetterItem[]> {
  const raw = (await idbGet(DEAD_LETTER_KEY)) as DeadLetterItem[] | undefined;
  return Array.isArray(raw) ? raw : [];
}

export async function clearDeadLetter(): Promise<void> {
  await idbDel(DEAD_LETTER_KEY);
}

/** Cheap unique id — good enough for queue keys and tempIds. */
export function newTempId(prefix = 'tmp'): string {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rand}`;
}
